import { toIntlLocale } from './date.functions';

export type tFormatPlanPauseDateOptions = {
  instant: string;
  /** The app language (`es`, `en`, `br`) or a full BCP-47 tag. */
  locale: string;
  style?: 'compact' | 'long';
};

/**
 * Plan-pause boundaries are Stripe billing instants represented as UTC dates.
 * Render every pause surface in UTC so the same boundary never appears as the
 * previous day for viewers west of Greenwich.
 *
 * Every surface hands in `i18n.language`, and `br` is Breton to `Intl`: a
 * Brazilian customer read "24 Gwengolo 2026". An app language goes through
 * `toIntlLocale`; a full tag is already what `Intl` needs.
 */
export function formatPlanPauseDate(
  options: tFormatPlanPauseDateOptions,
): string {
  const { instant, locale, style = 'compact' } = options;
  const parsed = new Date(instant);

  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat(
    locale.includes('-') ? locale : toIntlLocale(locale),
    style === 'long'
      ? { dateStyle: 'long', timeZone: 'UTC' }
      : {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        },
  ).format(parsed);
}

/**
 * The pause statuses that still hold the customer to a pause: ahead of it
 * (PENDING, SCHEDULED), inside it (ACTIVE), or inside one that ends with the
 * subscription (CANCEL_SCHEDULED).
 */
const SCHEDULED_OR_ACTIVE_PLAN_PAUSE_STATUSES: ReadonlySet<string> = new Set([
  'PENDING',
  'SCHEDULED',
  'ACTIVE',
  'CANCEL_SCHEDULED',
]);

/**
 * Whether the customer still has a scheduled or active pause. Billing keeps
 * projecting a paid early resume (`RESUME_PAID`) until the renewal that bills
 * the plan in full, but the customer paid to end that pause: it is over for
 * them (PD12), so cancelling does not end it and it does not stand in for the
 * retention offers. A status this front does not know is not a pause it can
 * speak for.
 */
export function isScheduledOrActivePlanPause(
  pause: { status: string } | null | undefined,
): boolean {
  return (
    pause !== null &&
    pause !== undefined &&
    SCHEDULED_OR_ACTIVE_PLAN_PAUSE_STATUSES.has(pause.status)
  );
}

export type tOutboundMessagingState = {
  planPause?: { inPauseWindow: boolean } | null;
  outboundMessagingAccess?: boolean | null;
};

/**
 * Whether the workspace cannot send outbound messages right now: its plan is
 * inside a pause window, or billing withheld the capability. The composer and
 * every other way to send (bulk, campaigns, auto-respond) read this one
 * predicate, so a partial or stale summary can never hide the composer while
 * another send surface stays open.
 */
export function isOutboundMessagingBlocked(
  subscription: tOutboundMessagingState,
): boolean {
  return (
    subscription.planPause?.inPauseWindow === true ||
    subscription.outboundMessagingAccess === false
  );
}
